import {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  ReactNode,
} from 'react';
import { authApi, setAuthFailureHandler } from '@services/api';
import { refreshAccessToken } from '@services/api/client';
import type { AuthUser, LoginCredentials } from '@types';
import toast from 'react-hot-toast';

interface AuthContextType {
  user: AuthUser | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  login: (credentials: LoginCredentials) => Promise<void>;
  logout: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider = ({ children }: AuthProviderProps) => {
  const [user, setUser] = useState<AuthUser | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  const isAuthenticated = !!user;

  // A failed refresh inside the axios interceptor drops the app's auth state.
  useEffect(() => {
    setAuthFailureHandler(() => setUser(null));
    return () => setAuthFailureHandler(null);
  }, []);

  /**
   * Bootstrap on mount / hard reload. The access token lives in memory only
   * (F1.2), so after a page reload it is gone — recover a session silently from
   * the httpOnly refresh cookie. No cookie / expired → stay logged out.
   */
  const bootstrap = useCallback(async () => {
    try {
      const token = await refreshAccessToken();
      if (token) {
        const currentUser = await authApi.getCurrentUser();
        setUser(currentUser);
      }
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    bootstrap();
  }, [bootstrap]);

  const login = async (credentials: LoginCredentials) => {
    // NOTE: never console.log token contents (readable via DevTools/XSS).
    try {
      const { user: loggedIn } = await authApi.login(credentials);
      setUser(loggedIn);
      toast.success('Signed in');
    } catch (error) {
      toast.error('Login failed. Please check your credentials.');
      throw error;
    }
  };

  const logout = async () => {
    try {
      await authApi.logout();
    } finally {
      setUser(null);
      toast.success('Signed out');
    }
  };

  const value = { user, isAuthenticated, isLoading, login, logout };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export default AuthContext;
